import React, { useState, useCallback, useEffect } from 'react';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import MapAddressPicker from "@/components/MapAddressPicker";

// --- Validation helpers ---

function validateAddressField(
  name: string,
  value: string | number | null | undefined,
  required: boolean
): string {
  const isPincode = name.endsWith('_pincode');
  const trimmed = String(value ?? '').trim();

  if (isPincode) {
    if (!trimmed) return 'Pincode is required';
    if (!/^\d+$/.test(trimmed)) return 'Pincode must contain digits only';
    if (trimmed.length !== 6) return 'Pincode must be exactly 6 digits';
    return '';
  }

  if (required && !trimmed) {
    const label = name.split('_address_')[1]?.replace(/_/g, ' ') || 'This field';
    return `${label.charAt(0).toUpperCase() + label.slice(1)} is required`;
  }

  return '';
}

// --- CompositeAddress Component ---

type AddressProps = {
  form?: any;
  prefix?: string;
  value?: any;
  onChange?: (val: any) => void;
  requiredFlags?: [boolean, boolean, boolean, boolean, boolean, boolean];
  disabled?: boolean;
  readOnly?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
};

export default function CompositeAddress({
  form,
  prefix = "",
  value,
  onChange,
  requiredFlags = [false, false, false, false, false, false],
  disabled = false,
  readOnly = false,
  registerSubmit,
}: AddressProps) {
  const [req1, req2, req3, req4, req5, req6] = requiredFlags;

  // Track which fields have been touched (blurred) for inline error display
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const fields = [
    { name: `${prefix}_address_line1`, label: "Flat / House no / Building name", required: req1 || true },
    { name: `${prefix}_address_line2`, label: "Address Line 2", required: req2 || true },
    { name: `${prefix}_address_line3`, label: "City / Town", required: req3 || true },
    { name: `${prefix}_address_landmark`, label: "Landmark", required: req4 },
    { name: `${prefix}_address_pincode`, label: "Pincode", required: req5 || true, maxLength: 6, numeric: true },
    { name: `${prefix}_address_state`, label: "State", required: req6 || true },
  ];

  // Register a submit handler that validates all fields before allowing Continue.
  // We use a ref to hold the latest value so the stable registerSubmit call always
  // sees the current field values without needing to re-register on every keystroke.
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const fieldsRef = React.useRef(fields);
  fieldsRef.current = fields;

  useEffect(() => {
    if (!registerSubmit || form) return;
    registerSubmit(async () => {
      const currentFields = fieldsRef.current;
      const currentValue = valueRef.current;

      // Touch all fields to show errors
      const allTouched: Record<string, boolean> = {};
      currentFields.forEach((f) => { allTouched[f.name] = true; });
      setTouched(allTouched);

      // Check if any required field has an error
      const hasErrors = currentFields.some((f) => {
        const raw = currentValue?.[f.name];
        const fieldValue = raw != null ? String(raw) : '';
        return validateAddressField(f.name, fieldValue, f.required) !== '';
      });

      return !hasErrors; // return false to block Continue
    });
  }, [registerSubmit]); // only run once when registerSubmit is provided

  const handleAddressSelect = (addr: {
    line1: string;
    line2: string;
    city: string;
    pincode: string;
    landmark: string;
    state?: string;
    lat?: number;
    lng?: number;
  }) => {
    const options = { shouldValidate: true, shouldDirty: true };

    // Keep line1 and line2 SEPARATE. Previously both were merged into line1 and
    // line2 was force-cleared, which left the (mandatory) Address Line 2 field
    // empty and forced the user to retype it.
    // MapAddressPicker returns line1 = house/premise/route and
    // line2 = sublocality/neighborhood.
    let line1 = addr.line1 || "";
    let line2 = addr.line2 || "";

    // Google does not always return a sublocality. Rather than leave the
    // mandatory Line 2 blank, split line1's trailing comma-segments into it
    // while keeping line1 long enough to pass its own validation (>=10 chars).
    if (!line2 && line1.includes(",")) {
      const parts = line1.split(",").map((p) => p.trim()).filter(Boolean);
      if (parts.length > 1) {
        const head: string[] = [];
        let i = 0;
        while (i < parts.length - 1 && head.join(", ").length < 10) {
          head.push(parts[i]);
          i += 1;
        }
        const rest = parts.slice(i);
        if (head.length && rest.length) {
          line1 = head.join(", ");
          line2 = rest.join(", ");
        }
      }
    }

    if (form) {
      form.setValue(`${prefix}_address_line1`, line1, options);
      form.setValue(`${prefix}_address_line2`, line2, options);
      form.setValue(`${prefix}_address_line3`, addr.city, options);
      form.setValue(`${prefix}_address_pincode`, addr.pincode, options);
      form.setValue(`${prefix}_address_landmark`, addr.landmark, options);
      form.setValue(`${prefix}_address_state`, addr.state || "", options);
    } else if (onChange) {
      onChange({
        ...value,
        [`${prefix}_address_line1`]: line1,
        [`${prefix}_address_line2`]: line2,
        [`${prefix}_address_line3`]: addr.city,
        [`${prefix}_address_pincode`]: addr.pincode,
        [`${prefix}_address_landmark`]: addr.landmark,
        [`${prefix}_address_state`]: addr.state || "",
        // Exact map-pin coordinates. Without these the backend has to re-geocode
        // the address text, which lands 50-500 m off and makes the selfie
        // location check unreliable.
        ...(addr.lat != null && addr.lng != null
          ? {
              [`${prefix}_address_latitude`]: addr.lat,
              [`${prefix}_address_longitude`]: addr.lng,
            }
          : {}),
      });
    }
    // Mark all fields as touched after map selection
    const allTouched: Record<string, boolean> = {};
    fields.forEach(f => { allTouched[f.name] = true; });
    setTouched(allTouched);
  };

  const allDisabled = disabled || readOnly;

  const handleBlur = (fieldName: string) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleControlledChange = (fieldName: string, rawValue: string) => {
    let cleanValue = rawValue;
    // Pincode: digits only, max 6
    if (fieldName.endsWith('_pincode')) {
      cleanValue = rawValue.replace(/\D/g, '').slice(0, 6);
    }
    onChange?.({ ...value, [fieldName]: cleanValue });
  };

  // Use local state if no form is provided (Controlled mode for Builder / JourneyPage)
  if (!form) {
    return (
      <div className="space-y-3">
        {!allDisabled && (
          <div className="flex justify-center mb-3">
            <MapAddressPicker onAddressSelect={handleAddressSelect} />
          </div>
        )}
        {fields.map((f) => {
          const rawFieldValue = value?.[f.name];
          const fieldValue = rawFieldValue != null ? String(rawFieldValue) : '';
          const error = touched[f.name]
            ? validateAddressField(f.name, fieldValue, f.required)
            : '';

          return (
            <div key={f.name} className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">
                {f.label}{' '}
                {f.required ? <span className="text-red-500">*</span> : null}
              </label>
              <Input
                disabled={allDisabled}
                placeholder={f.label}
                maxLength={f.maxLength}
                inputMode={f.numeric ? "numeric" : "text"}
                value={fieldValue}
                onChange={(e) => handleControlledChange(f.name, e.target.value)}
                onBlur={() => handleBlur(f.name)}
                className={`h-9 ${error ? 'border-red-400 focus:border-red-500' : ''}`}
              />
              {error && (
                <p className="text-xs text-red-500 mt-0.5">{error}</p>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {fields.map((f, index) => (
        <FormField
          key={f.name}
          control={form.control}
          name={f.name}
          render={({ field }) => (
            <FormItem>
              {index === 0 && !allDisabled && (
                <div className="flex justify-center mb-3">
                  <MapAddressPicker onAddressSelect={handleAddressSelect} />
                </div>
              )}
              <FormLabel className="text-gray-700">
                {f.label} {f.required ? <span className="text-red-500">*</span> : null}
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  disabled={allDisabled}
                  maxLength={f.maxLength}
                  inputMode={f.numeric ? "numeric" : "text"}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (f.name.endsWith('_pincode')) {
                      val = val.replace(/\D/g, '').slice(0, 6);
                    }
                    field.onChange(val);
                  }}
                  className="h-9"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </div>
  );
}

// --- Specialized Exports ---

export const CompositePersonalCommunicationAddress = (props: any) => <CompositeAddress {...props} prefix="communication" />;
export const CompositePersonalResidentialAddress = (props: any) => <CompositeAddress {...props} prefix="residential" />;
export const CompositeBusinessAddress = (props: any) => <CompositeAddress {...props} prefix="business" />;
export const CompositeBusinessRegisteredAddress = (props: any) => <CompositeAddress {...props} prefix="registered" />;
